// B1.3: auth domain methods (move-only from client.ts).
import { buildFallbackBootstrap, type AppBootstrap } from '@/lib/access';
import { ApiClientCore, buildQueryString } from './http/core';

export class AuthApi extends ApiClientCore {
  async register(data: {
    email: string;
    password: string;
    full_name?: string;
    last_name?: string;
    first_name?: string;
    middle_name?: string;
    phone?: string;
    organization_name?: string;
    /** Только при регистрации новой организации (без invite). */
    product_vertical?: 'pvz' | 'hospitality';
    accept_privacy_policy: boolean;
    privacy_policy_version: string;
  }): Promise<
    | {
        verification_required: true;
        email_masked?: string;
        email: string;
        email_delivery_failed?: boolean;
      }
    | { verification_required: false; access_token?: string; refresh_token?: string; user: any; bootstrap?: AppBootstrap }
  > {
    const response = await this.post<{
      verification_required?: boolean;
      email_masked?: string;
      email_delivery_failed?: boolean;
      access_token?: string;
      refresh_token?: string;
      user?: any;
    }>('/api/v1/auth/register', data);

    if (response.verification_required) {
      return {
        verification_required: true,
        email_masked: response.email_masked,
        email: data.email,
        email_delivery_failed: response.email_delivery_failed,
      };
    }

    if (typeof window !== 'undefined' && response.user) {
      // Cookie-only: backend выставил HttpOnly-cookies, токены из тела не сохраняем.
      // Чистим возможные legacy-ключи на всякий случай.
      this.clearLegacyLocalStorageTokensOnce();
      localStorage.setItem('user', JSON.stringify(response.user));
      const bootstrap = await this.getBootstrap().catch(() => buildFallbackBootstrap(response.user));
      localStorage.setItem('web_admin_bootstrap', JSON.stringify(bootstrap));
      return {
        verification_required: false,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        user: response.user,
        bootstrap,
      };
    }

    return {
      verification_required: false,
      access_token: response.access_token,
      refresh_token: response.refresh_token,
      user: response.user!,
    };
  }
  async verifyEmail(data: { email: string; code: string }): Promise<{
    access_token?: string;
    refresh_token?: string;
    user: any;
    bootstrap?: AppBootstrap;
  }> {
    const response = await this.post<{ access_token?: string; refresh_token?: string; user: any }>(
      '/api/v1/auth/verify-email',
      data
    );
    if (typeof window !== 'undefined') {
      this.clearLegacyLocalStorageTokensOnce();
      localStorage.setItem('user', JSON.stringify(response.user));
      const bootstrap = await this.getBootstrap().catch(() => buildFallbackBootstrap(response.user));
      localStorage.setItem('web_admin_bootstrap', JSON.stringify(bootstrap));
      return { ...response, bootstrap };
    }
    return response;
  }
  async resendVerification(email: string): Promise<void> {
    await this.post('/api/v1/auth/resend-verification', { email });
  }
  async login(email: string, password: string) {
    const response = await this.post<{ access_token?: string; refresh_token?: string; user: any }>(
      '/api/v1/auth/login',
      { email, password }
    );

    if (typeof window !== 'undefined') {
      this.clearLegacyLocalStorageTokensOnce();
      localStorage.setItem('user', JSON.stringify(response.user));
      const bootstrap = await this.getBootstrap().catch(() => buildFallbackBootstrap(response.user));
      localStorage.setItem('web_admin_bootstrap', JSON.stringify(bootstrap));
      return { ...response, bootstrap };
    }

    return response as typeof response & { bootstrap?: AppBootstrap };
  }
  async issueWsTicket(): Promise<{ ticket: string; expires_in: number }> {
    return this.post('/api/v1/auth/ws-ticket', {});
  }
  async ensureSession(_forceRefresh = false): Promise<string | null> {
    if (typeof window === 'undefined') return null;
    const refreshed = await this.refreshSession();
    if (refreshed === null) return null;
    return '__cookie_session__';
  }
  logout() {
    if (typeof window === 'undefined') return;
    void fetch(`${this.baseURL}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Client': 'web' },
      keepalive: true,
    }).catch(() => {});
    this.handleAuthFailure('manual');
  }
  async getBootstrap(): Promise<AppBootstrap> {
    return this.get<AppBootstrap>('/api/v1/permissions/bootstrap');
  }
  async getMe(): Promise<any> {
    return this.get('/api/v1/auth/me');
  }
  async updateMe(data: {
    full_name?: string | null;
    last_name?: string | null;
    first_name?: string | null;
    middle_name?: string | null;
    phone?: string | null;
    avatar_file_id?: number | null;
  }): Promise<any> {
    const response = await this.put('/api/v1/auth/me', data);
    if (typeof window !== 'undefined') {
      const currentUser = this.getUser();
      if (currentUser) {
        localStorage.setItem('user', JSON.stringify({ ...currentUser, ...response }));
      }
    }
    return response;
  }
  async sendEmployeePasswordReset(employeeId: number): Promise<{ ok: boolean; email_masked?: string; email_delivery_failed?: boolean }> {
    return this.post(`/api/v1/employees/${employeeId}/send-password-reset`, {});
  }
  // ─── Compliance ─────────────────────────────────────────────────────────────
  async getComplianceSummary(): Promise<any> {
    return this.get('/api/v1/compliance/summary');
  }
  async getComplianceByLocation(): Promise<any> {
    return this.get('/api/v1/compliance/by-location');
  }
  async getTopComplianceIssues(limit = 10): Promise<any> {
    return this.get(`/api/v1/compliance/top-issues${buildQueryString({ limit })}`);
  }
  async getComplianceByCategory(): Promise<any> {
    return this.get('/api/v1/compliance/by-category');
  }
  async getComplianceTrend(days = 30): Promise<any> {
    return this.get(`/api/v1/compliance/trend${buildQueryString({ days })}`);
  }
  async refreshComplianceCache(): Promise<any> {
    return this.post('/api/v1/compliance/refresh-cache');
  }
}
